import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { Payload } from "recharts/types/component/DefaultLegendContent";
import Icon from "../icon/Icon";
import { DAY, getWeekRange, xAxisTickFormatter } from "./helpers";

const DataPoint = ({ payload }: { payload: Payload }) => {
  const { t } = useTranslation();
  const value = payload.payload?.value || payload?.value || "0";
  const color =
    payload.payload?.fill || payload.color || payload.payload?.color;
  const opacity =
    payload.payload?.opacity || payload.opacity || payload.payload?.opacity;
  let label =
    payload.payload?.legendReference ||
    (payload.name &&
      typeof payload.name !== "number" &&
      payload.name !== "value" &&
      `${t(payload.name)}`);
  if (label === payload.title) label = undefined;

  return (
    <p className="body-sm -ml-0.5 flex items-center gap-1 hyphens-auto">
      <Icon
        name={payload.type === "line" ? "minus-md" : "point-md"}
        className="shrink-0"
        style={{ color, opacity }}
      />
      {[label, value].filter(Boolean).join(": ")}
    </p>
  );
};
interface ChartTooltipProps {
  view?: "weeks" | "days";
  active?: boolean;
  payload?: Payload[];
  label?: number;
  subLabel?: string;
}
export const ChartTooltip = ({
  view,
  active,
  payload,
  label,
  subLabel,
}: ChartTooltipProps) => {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) return null;

  let title = label?.toString();
  let subTitle = subLabel;

  if (label) {
    title = xAxisTickFormatter({
      value: label,
      view,
      locale: i18n.resolvedLanguage,
      longDayFormat: view === "days",
    });
    subTitle = view === "weeks" ? getWeekRange(label * DAY) : subTitle;
  }
  const [item] = payload || [];
  if (item) {
    const {
      payload: { text, label, valueLabel },
    } = item || { payload: {} };
    title = text || label || title;
    subTitle = subTitle || valueLabel;
  }
  return (
    <div className="bg-surface-default border-border-default flex min-w-[100px] max-w-xs flex-col gap-2 rounded-xl border p-3 shadow-lg z-50">
      <div className="flex flex-col">
        <p className="body-md-bold hyphens-auto">{title}</p>
        {subTitle && (
          <p className={"body-sm text-text-subdued hyphens-auto"}>{subTitle}</p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {payload?.map((props) => (
          <DataPoint payload={props} key={props.name} />
        ))}
      </div>
    </div>
  );
};
